const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';
const REQUIRE_CONSENT = process.env.REACT_APP_GA4_REQUIRE_CONSENT === 'true';
const CONSENT_STORAGE_KEY = 'analytics_consent';

let initialized = false;
let scriptLoaded = false;
let lastTrackedPage = '';

const hasWindow = () => typeof window !== 'undefined';

const getConsentState = () => {
  if (!hasWindow()) {
    return 'denied';
  }
  return localStorage.getItem(CONSENT_STORAGE_KEY) || 'unknown';
};

const hasConsent = () => !REQUIRE_CONSENT || getConsentState() === 'granted';

const canTrack = () => Boolean(GA4_MEASUREMENT_ID) && hasConsent();

const normalizeParams = (params = {}) => {
  const sanitized = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }
    if (typeof value === 'string' && value.length > 256) {
      sanitized[key] = value.slice(0, 256);
      return;
    }
    sanitized[key] = value;
  });
  return sanitized;
};

const injectScript = () => {
  if (!hasWindow() || scriptLoaded) {
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  script.onload = () => {
    scriptLoaded = true;
  };
  document.head.appendChild(script);
};

export const initAnalytics = () => {
  if (!hasWindow() || initialized || !canTrack()) {
    return;
  }

  injectScript();

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtagProxy() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA4_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  initialized = true;
};

export const grantAnalyticsConsent = () => {
  if (!hasWindow()) {
    return;
  }
  localStorage.setItem(CONSENT_STORAGE_KEY, 'granted');
  initialized = false;
  initAnalytics();
};

export const denyAnalyticsConsent = () => {
  if (!hasWindow()) {
    return;
  }
  localStorage.setItem(CONSENT_STORAGE_KEY, 'denied');
};

export const trackEvent = (eventName, params = {}) => {
  if (!canTrack()) {
    return;
  }
  initAnalytics();
  if (typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', eventName, normalizeParams(params));
};

export const trackPageView = ({ pagePath, pageTitle }) => {
  if (!canTrack()) {
    return;
  }
  initAnalytics();
  if (typeof window.gtag !== 'function') {
    return;
  }

  const fullPath = pagePath || `${window.location.pathname}${window.location.search}`;
  const dedupeKey = `${fullPath}::${pageTitle || document.title || ''}`;
  if (dedupeKey === lastTrackedPage) {
    return;
  }
  lastTrackedPage = dedupeKey;

  window.gtag('event', 'page_view', {
    page_path: fullPath,
    page_location: window.location.href,
    page_title: pageTitle || document.title || '',
  });
};

export const analyticsConfig = {
  measurementId: GA4_MEASUREMENT_ID,
  requireConsent: REQUIRE_CONSENT,
  consentStorageKey: CONSENT_STORAGE_KEY,
};
